import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BookOpen, NotebookPen, Plus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { AlbumCover } from '@/components/AlbumCover'
import { FormError } from '@/components/FormError'
import { Steps } from '@/components/Steps'
import { ShareWith } from '@/components/ShareWith'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import type { Family, Space } from '@/lib/families'
import { photoUrl } from '@/lib/photos'
import { NO_BOOK, sendRecipes, useBooks, type BookSummary } from '@/lib/recipes'
import { cn } from '@/lib/utils'
import { JustSeeing } from '@/components/JustSeeing'
import { usePosts } from '@/lib/sections'
import { audienceBody, startingAudience, useLastShare, type Audience } from '@/lib/spaces'

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
const cardUrl = (id: string) => photoUrl(id, 'thumb')

// The recipe books in the space you're in, shelved the way Photos shelves albums: each book's front shows its
// newest cards together. A family can keep as many as it likes: Grandma's recipes, holiday baking, and so on. With
// `mine`, only the books you started. A new book is started from the page (onStart), which owns the "+".
export function BooksSection({
  family,
  space,
  mine = false,
  onStart,
}: {
  family: Family
  space: Space | null
  mine?: boolean
  onStart: () => void
}) {
  const books = useBooks(family.id, space?.id)
  const all = books.data?.books ?? []
  const list = mine ? all.filter((x) => x.mine) : all
  const first = books.data !== undefined && all.length === 0
  // The recipes in no book, shelved after the books like one more.
  const loose = (mine ? books.data?.loose?.mine : books.data?.loose?.recipes) ?? 0
  const looseShelf = loose > 0 && (
    <li>
      <Link
        to={`/circle/${family.id}/recipes?${new URLSearchParams(mine ? { loose: '1', mine: '1' } : { loose: '1' })}`}
        className={cn('group flex flex-col gap-3 rounded-[1.75rem]', link)}
      >
        <AlbumCover covers={[]} index={2} url={cardUrl} icon={NotebookPen} />
        <span className="flex flex-col gap-0.5 px-1">
          <span className="truncate font-semibold">{NO_BOOK}</span>
          <span className="truncate text-sub text-muted-foreground">
            {loose} {loose === 1 ? 'recipe' : 'recipes'}
          </span>
        </span>
      </Link>
    </li>
  )
  return (
    <section className="flex flex-col gap-6" aria-labelledby="books">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 id="books" className="text-title">
          Recipe books
        </h2>
        {/* On a phone, new books come from the top bar's "+". */}
        {all.length > 0 && (
          <Button size="lg" className="max-nav:hidden" onClick={onStart}>
            <Plus aria-hidden /> New book
          </Button>
        )}
      </div>
      <FormError error={books.error} />
      {first && <FirstBook onStart={onStart} />}
      {first && looseShelf && (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-5">
          {looseShelf}
        </ul>
      )}
      {!first && list.length === 0 && <p className="text-muted-foreground">You haven’t started a book yet.</p>}
      {list.length > 0 && (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-5">
          {list.map((book, i) => (
            <li key={book.id}>
              <BookFront book={book} index={i} />
            </li>
          ))}
          {looseShelf}
          <li>
            <button
              type="button"
              onClick={onStart}
              className={cn('group flex w-full flex-col gap-3 rounded-[1.75rem]', link)}
            >
              <span className="grid aspect-[4/5] w-full place-items-center rounded-[1.75rem] bg-secondary transition-colors group-hover:bg-accent">
                <span className="grid size-12 place-items-center rounded-full bg-background shadow-paper transition-transform group-hover:scale-105">
                  <Plus className="size-6" aria-hidden />
                </span>
              </span>
              <span className="px-1 text-left font-medium">Start a book</span>
            </button>
          </li>
        </ul>
      )}
    </section>
  )
}

function BookFront({ book, index }: { book: BookSummary; index: number }) {
  // Before the API sent several fronts, a book had one cover; either way the front shows what's there.
  const covers = book.covers ?? (book.cover ? [book.cover] : [])
  return (
    <Link to={`/book/${book.id}`} className={cn('group flex flex-col gap-3 rounded-[1.75rem]', link)}>
      <AlbumCover covers={covers} index={index} url={cardUrl} icon={BookOpen} />
      <span className="flex flex-col gap-0.5 px-1">
        <span className="truncate font-semibold">{book.title}</span>
        <span className="truncate text-sub text-muted-foreground">
          {book.recipes} {book.recipes === 1 ? 'recipe' : 'recipes'}
        </span>
        {book.forEveryone && <span className="truncate text-sub text-muted-foreground">Shared with Everyone</span>}
      </span>
    </Link>
  )
}

function FirstBook({ onStart }: { onStart: () => void }) {
  const posting = usePosts('recipes')
  return (
    <div className="flex flex-col items-center gap-6 rounded-[2.5rem] table-surface px-6 py-12 text-center sm:py-16">
      <AlbumCover covers={[]} index={1} icon={BookOpen} className="w-36 sm:w-44" />
      <div className="flex max-w-md flex-col gap-2">
        <p className="text-title font-semibold tracking-tight text-balance">Start the first recipe book.</p>
        <p className="text-table-muted">
          One book for each cook or each kind of cooking. Photograph the cards first and type them out later, or never.
        </p>
      </div>
      {posting ? (
        <Button size="lg" onClick={onStart}>
          <Plus aria-hidden /> Start the first book
        </Button>
      ) : (
        <JustSeeing />
      )}
      <p className="whisper">Keep the card. Share the recipe.</p>
    </div>
  )
}

const IDEAS = ['Grandma’s recipes', 'Holiday baking', 'Weeknights', 'Sunday suppers']

type Step = 'name' | 'who'

// Starting a book, a step at a time like starting an album: what it's called, then who it's for (starting from the
// space you're in). Then straight in to add the first card.
export function NewBook({
  open,
  onOpenChange,
  family,
  space,
  first,
  send,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  family: Family
  space: Space | null
  first: boolean
  // Recipes picked to send to the new book once it's made.
  send?: string[]
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const steps: Step[] = ['name', 'who']
  const [step, setStep] = useState<Step>('name')
  const [title, setTitle] = useState('')
  const lastShare = useLastShare()
  const [who, setWho] = useState<Audience>(() => lastShare.start(startingAudience(family, space)))
  const at = steps.indexOf(step)
  const last = at === steps.length - 1
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>(`/families/${family.id}/books`, {
        method: 'POST',
        body: { title: title.trim(), ...audienceBody(who) },
      }),
    onSuccess: async ({ id }) => {
      lastShare.remember(who)
      if (send?.length) await sendRecipes(family.id, send, id)
      await queryClient.invalidateQueries({ queryKey: ['books', family.id] })
      if (send?.length) await queryClient.invalidateQueries({ queryKey: ['recipe'] })
      navigate(`/book/${id}`)
    },
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (last) create.mutate()
    else setStep(steps[at + 1]!)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-6">
          <DialogHeader>
            <p className="eyebrow">
              {first ? 'The first book' : 'New book'}
              {steps.length > 1 && ` · Step ${at + 1} of ${steps.length}`}
            </p>
            <DialogTitle>{step === 'name' ? 'What’s it called?' : 'Who’s this for?'}</DialogTitle>
            <DialogDescription>
              {step === 'name'
                ? 'Name it for the cook or the kind of cooking. You can rename it any time.'
                : 'Only the people you pick will see it, and what’s in it. You can change this any time.'}
            </DialogDescription>
          </DialogHeader>
          {steps.length > 1 && <Steps n={steps.length} at={at} />}

          {step === 'name' && (
            <div className="grid gap-3">
              <Label htmlFor="book-title">Book name</Label>
              <Input id="book-title" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
              <div className="flex flex-wrap gap-2" aria-label="Ideas">
                {IDEAS.map((idea) => (
                  <Button key={idea} type="button" variant="outline" size="sm" onClick={() => setTitle(idea)}>
                    {idea}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {step === 'who' && (
            <ShareWith here={family.id} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
          )}

          <FormError error={create.error} />
          <div className="flex flex-row-reverse flex-wrap justify-between gap-2">
            <Button type="submit" size="lg" disabled={create.isPending || (step === 'name' && !title.trim())}>
              {last ? 'Start book' : 'Next'}
            </Button>
            {at > 0 && (
              <Button type="button" variant="ghost" size="lg" onClick={() => setStep(steps[at - 1]!)}>
                Back
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
